/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  Box,
  Flex,
  Space,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableScrollbar,
  Text,
  useColorMode,
  useColorStyle,
  Stack,
  Link,
} from '@tonic-one/react';

import MiniPagination from '../../Report/MiniPagination';
import AddButton from './AddButton';
import { useDelegatorAccount } from '../utils/hooks';
import SingleOrgTable from './SingleOrgTable';
import { Trans, useTranslation } from 'react-i18next';
import { AppData, SaModalNameEnum, SaModalState, SelectedRow } from './types';
import { AppForSa, ServiceForReAuth } from '@Types/administrator';
import CSpinner from '../../../components/CSpinner';
import SAModal from './SAModal';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import RedirectLink from '@Components/RedirectLink';
import { useLocation } from 'react-router-dom';
import { useNavigateWithHandler } from '@Utils/hooks';

function ServiceAccount() {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const navigate = useNavigateWithHandler();
  const location = useLocation();
  const { t } = useTranslation('administration');
  const { accounts, reFetchData, loading } = useDelegatorAccount();
  const [page, setPage] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  const accountWithApp = accounts.filter((acc) => acc.apps.length !== 0);
  const currentAccounts = accountWithApp.slice(
    (page.currentPage - 1) * page.pageSize,
    page.currentPage * page.pageSize
  );

  const [saModalState, setSaModalState] = useState<SaModalState>(null);
  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null);
  const { widthEditablePermission } = usePermissionStore();
  const removeBtnDisabled =
    selectedRow === null ||
    (selectedRow !== null &&
      accounts.find((a) => a.orgId === selectedRow.orgId)?.deprovisioning);

  // Auto-open ReAuth modal when navigated from ExchangeOnline settings page
  const reauthState = location.state as {
    reauth?: string;
    orgId?: string;
  } | null;

  useEffect(() => {
    if (
      !reauthState?.reauth ||
      !reauthState?.orgId ||
      loading ||
      accounts.length === 0
    ) {
      return;
    }

    const serviceMap: Record<
      string,
      { app: AppForSa; service: ServiceForReAuth }
    > = {
      exchange: { app: AppForSa.Exchange, service: ServiceForReAuth.Exchange },
    };

    const mapping = serviceMap[reauthState.reauth];
    if (!mapping) return;

    const account = accounts.find((a) => a.orgId === reauthState.orgId);
    const appData: AppData | undefined = account?.apps.find(
      (a) => a.app === mapping.app
    );

    if (appData) {
      setSaModalState({
        name: SaModalNameEnum.ReAuth,
        data: {
          orgId: reauthState.orgId,
          service: mapping.service,
          authAccount: appData.email,
          provisionType: appData.provisionType,
        },
      });
    }

    // Clear state to prevent re-triggering on refresh or back navigation
    navigate(location.pathname, { replace: true, state: {} });
  }, [reauthState, loading, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshData = () => {
    //init state
    setSaModalState(null);
    setSelectedRow(null);
    setPage({
      currentPage: 1,
      pageSize: 10,
    });
    reFetchData();
  };
  return (
    <>
      {loading && <CSpinner />}
      <Flex
        maxHeight="100%"
        height="100%"
        position="relative"
        direction="column"
        overflow="hide"
      >
        <Box flexShrink="0" px="16px">
          <Text py="16px" size="lg">
            {t('administration.sa.title')}
          </Text>
          <Text color={colorStyle.color.secondary}>
            <Trans
              i18nKey="administration.sa.linkToServiceStatus"
              ns="administration"
            >
              placeholder
              <Link
                onClick={() => {
                  navigate('/dashboard?type=provision');
                }}
              >
                placeholder
              </Link>
            </Trans>
          </Text>

          <Space height="16px" display="block" />

          <AddButton
            accounts={accounts}
            refreshData={refreshData}
            setSaModalState={setSaModalState}
          />
          <Space width="16px" />
          <Button
            variant="primary"
            disabled={
              removeBtnDisabled ||
              !widthEditablePermission(PermissionNameEnum.administrator)
            }
            onClick={() => {
              if (!selectedRow) return;
              setSaModalState({
                name: SaModalNameEnum.Deprovision,
                data: selectedRow,
              });
            }}
          >
            {t('common.action.label.remove')}
          </Button>
        </Box>
        <Box flexGrow="1" overflow="hide">
          <Table width="100%" height="100%" size="lg">
            <SAHeader />
            <TableScrollbar height="100%" overflow="visible">
              <TableBody width="100%">
                {currentAccounts.map((account) => (
                  <SingleOrgTable
                    key={account.orgId}
                    account={account}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    setSaModalState={setSaModalState}
                  />
                ))}
              </TableBody>
            </TableScrollbar>
          </Table>
        </Box>
        <Flex
          align="center"
          justify="flex-end"
          height="48px"
          background={colorStyle.background.secondary}
          width="100%"
        >
          <MiniPagination
            totalCount={accountWithApp.length}
            pageSize={page.pageSize}
            currentPage={page.currentPage}
            changeCurrentPage={(currentPage: number) =>
              setPage({ ...page, currentPage })
            }
            changePageSize={(pageSize: number) =>
              setPage({ ...page, currentPage: 1, pageSize })
            }
          />
        </Flex>
      </Flex>
      {saModalState && (
        <SAModal
          saModalState={saModalState}
          onClose={() => {
            setSaModalState(null);
          }}
          refreshData={refreshData}
        />
      )}
    </>
  );
}

const SAHeader = memo(() => {
  const { t } = useTranslation('administration');
  return (
    <TableHeader width="100%">
      <TableHeaderRow width="100%">
        <TableHeaderCell width="32px" />
        <TableHeaderCell width="40%" flexGrow="2">
          {t('administration.sa.table.header.name')}
        </TableHeaderCell>
        <TableHeaderCell width="15%" flexGrow="1">
          {t('administration.sa.table.header.serviceType')}
        </TableHeaderCell>
        <TableHeaderCell width="15%" flexGrow="1">
          {t('administration.sa.table.header.expirationDate')}
        </TableHeaderCell>
        <TableHeaderCell width="30%" flexGrow="2">
          {t('administration.sa.table.header.status')}
        </TableHeaderCell>
      </TableHeaderRow>
    </TableHeader>
  );
});

export default ServiceAccount;
